import {
  AfterViewInit,
  Component,
  forwardRef,
  Input,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR} from '@angular/forms';
import * as _ from 'lodash-es';
import {normalizeColumnsConfig, sortedIncludeColumnConfigByKey, TableColumn, TableColumnConfig} from '../tables/table';
import {DisplayColumn, DisplayColumnsFactory} from './display-columns.factory';

interface FormControls {
    [key: string]: FormControl; // To be able to typecast with FormGroup.controls
}

interface FieldsColumnTemplateContext {
    $implicit: DisplayColumn[];
}

@Component({
    selector: 'columns-manager',
    templateUrl: './columns-manager.component.html',
    styleUrls: ['./columns-manager.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ColumnsManagerComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ColumnsManagerComponent implements ControlValueAccessor, AfterViewInit {

    @Input()
    public columns: TableColumn[];

    @Input()
    public title: string;

    public form: FormGroup;

    public propagateChange: (columnsSettings: TableColumnConfig[]) => void;

    private columnsSettings: TableColumnConfig[];

    private displayColumns: DisplayColumn[] = [];

    @ViewChild('fieldsColumnTemplate', {read: TemplateRef, static: true})
    private fieldsColumnTemplateRef: TemplateRef<FieldsColumnTemplateContext>;

    @ViewChildren('fieldsColumnPlaceholder', {read: ViewContainerRef})
    private fieldsColumnPlaceholderList: QueryList<ViewContainerRef>;

    private isViewInitialized: boolean = false;

    public writeValue(columnsSettings: TableColumnConfig[]): void {
        this.columnsSettings = columnsSettings;
        this.displayColumns = DisplayColumnsFactory.createDisplayColumns(this.columns, this.columnsSettings);
        this.buildForm();

        if (this.isViewInitialized) {
            this.renderControls();
        }
    }

    public registerOnChange(fn: (columnsSettings: TableColumnConfig[]) => void): void {
        this.propagateChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        // To be overridden.
    }

    public ngAfterViewInit(): void {
        this.renderControls();

        this.isViewInitialized = true;
    }

    private buildForm(): void {
        const formControls: FormControls = {};

        this.displayColumns.forEach(displayColumn => {
            formControls[displayColumn.key] = this.createColumnFormControl(displayColumn);
        });

        this.form = new FormGroup(formControls);
    }

    private createColumnFormControl(displayColumn: DisplayColumn): FormControl {
        const control: FormControl = new FormControl(displayColumn.isVisible);

        control.valueChanges.subscribe(isVisible => {
            displayColumn.isVisible = isVisible;

            if (displayColumn.isGroupedColumn) {
                this.displayColumns.forEach(currentDisplayColumn => {
                    const isChangeNeeded = currentDisplayColumn.columnDef.groupId === displayColumn.columnDef.groupId
                        && currentDisplayColumn.isVisible !== isVisible;

                    if (isChangeNeeded) {
                        currentDisplayColumn.isVisible = isVisible;

                        this.form.controls[currentDisplayColumn.key].setValue(isVisible, {emitEvent: false});
                    }
                });
            }

            const columnsConfig = this.getColumnsConfigByDisplayColumns();
            this.propagateChange(columnsConfig);
        });

        return control;
    }

    private getColumnsConfigByDisplayColumns(): TableColumnConfig[] {
        const columnConfigDict = _.keyBy(this.columnsSettings, columnConfig => columnConfig.key);
        const columnsConfig = this.displayColumns.reduce((resultColumnsSettings, displayColumn) => {
            if (displayColumn.isVisible !== !!columnConfigDict[displayColumn.key]) {
                if (displayColumn.isVisible) {
                    resultColumnsSettings = sortedIncludeColumnConfigByKey(
                        this.columns,
                        resultColumnsSettings,
                        displayColumn.key
                    );
                } else {
                    const columnConfigIndex = resultColumnsSettings
                        .findIndex(columnConfig => columnConfig.key === displayColumn.key);

                    if (columnConfigIndex >= 0) {
                        resultColumnsSettings.splice(columnConfigIndex, 1);
                    }
                }
            }

            return resultColumnsSettings;
        }, this.columnsSettings.slice());

        return normalizeColumnsConfig(this.columns, columnsConfig);
    }

    private renderControls(): void {
        const fieldsColumnRefs = this.fieldsColumnPlaceholderList.toArray();
        const columnGroups = this.groupItems<DisplayColumn>(
            this.displayColumns,
            displayColumn => displayColumn.columnDef.groupId
        );
        const columnGroupsChunks = this.getBalancedChunks<DisplayColumn[]>(
            columnGroups,
            fieldsColumnRefs.length,
            (columnGroup: TableColumn[]) => columnGroup.length
        );

        columnGroupsChunks.forEach((columnGroupChunk, iChunk) => {
            const chunkDisplayColumns: DisplayColumn[] = columnGroupChunk.reduce(
                (currentColumns, group) => currentColumns.concat(group),
                [] as DisplayColumn[]
            );
            const chunkViewContainerRef = fieldsColumnRefs[iChunk];

            chunkViewContainerRef.clear();
            chunkViewContainerRef.createEmbeddedView(this.fieldsColumnTemplateRef, {
                $implicit: chunkDisplayColumns
            });
        });
    }

    /**
     * Group input items by a custom criterion.
     * @param {T[]} allItems
     * @param {(item: T) => any} getItemGroupId
     * @returns {T[][]}
     */
    private groupItems<T>(allItems: T[], getItemGroupId: (item: T) => any): T[][] {
        const createGroup = (groupId: string) => allItems
            .filter(item => getItemGroupId(item) === groupId);

        return allItems
            .reduce((groupingMeta, item) => {
                const groupId = getItemGroupId(item);
                const isGroupedColumn: boolean = groupId !== null && groupId !== undefined;

                if (isGroupedColumn) {
                    if (!groupingMeta.groupKnowledgeById.has(groupId)) {
                        const group = createGroup(groupId);

                        groupingMeta.groupKnowledgeById.add(groupId);
                        groupingMeta.groups.push(group);
                    }
                } else {
                    groupingMeta.groups.push([item]);
                }

                return groupingMeta;
            }, {
                groups: [],
                groupKnowledgeById: new Set()
            } as {
                groups: T[][];
                groupKnowledgeById: Set<any>;
            }).groups;
    }

    /**
     * Splits input array into N chunks.
     * The chunks are balanced by their weights:
     * - weight of each item is calculated using the passed function;
     * - weight of a chunk equals to sum of weights of all its items;
     * - all chunks have similar weight;
     * - each chunk is balanced by weight moving some items between chunks.
     * @param {T[]} values array of the input items
     * @param {number} chunkCount specifies how many chunks should the array be split into.
     * @param {(value: T) => number} calcValueWeight function should return weight of the passed item.
     * @returns {T[][]} the input array split to chunks.
     */
    private getBalancedChunks<T>(values: T[], chunkCount: number, calcValueWeight: (value: T) => number): T[][] {
        interface Item {
            value: T;
            weight: number;
            iItem: number;
        }

        interface Chunk {
            items: Item[];
            weight: number;
        }

        const allItems: Item[] = values.map((value, iItem): Item => ({
            value,
            weight: calcValueWeight(value),
            iItem
        }));

        const allItemWeights: number[] = allItems.map(item => item.weight);
        const avgChunkWeight = Math.ceil(_.sum(allItemWeights) / chunkCount);
        const normalChunkWeight = Math.max(avgChunkWeight, ...allItemWeights);

        const itemsLeft: Item[] = allItems.slice();

        const chunks: Chunk[] = _.times(chunkCount, (): Chunk => {
            const chunk: Chunk = {
                items: [],
                weight: 0
            };

            let iItem = 0;

            while (iItem < itemsLeft.length) {
                const item = itemsLeft[iItem];

                if (chunk.weight + item.weight <= normalChunkWeight) {
                    chunk.items.push(item);
                    chunk.weight += item.weight;

                    itemsLeft.splice(iItem, 1);

                    if (chunk.weight === normalChunkWeight) {
                        break;
                    }
                } else {
                    iItem++;
                }
            }

            return chunk;
        }).sort((chunk1, chunk2) => chunk2.weight - chunk1.weight); // DESC

        return chunks.map(chunk => chunk.items.map(item => item.value));
    }

}
